import { TourModel } from 'api-hooks/tour/tour-list/tour-detail';
import classNames from 'classnames';
import { EnumList } from 'common/constants/enum';
import stylesBreakpoint from 'common/styles/styles.breakpoint';
import { themedStyled } from 'common/styles/styletron.theme';
import Separator from 'components/common/separator';
import { Input } from 'components/elements';
import { useFormState } from 'components/elements/form';
import { FullContainer, HalfContainer } from 'components/styles';
import { differenceInCalendarDays } from 'date-fns';
import EmployeeSelectInput from 'legacy-modules/master/select-input/employee-select-input';
import EnumSelectInput from 'legacy-modules/master/select-input/enum';
import TourCategorySelectInput from 'legacy-modules/master/tour-category/components/select-input';
import TransactionAt from 'legacy-modules/sales/component/transaction-at';
import {
  OneThirdContainer,
  SectionContainer as ModuleSectionContainer,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import OtherPriceSection from './other-price';
import TourPriceSection from './tour-price';
import { SectionContainer } from '../../style';
import { TourListFormType } from '../form-type';
import styles from '../styles.module.css';

interface Props {
  tour?: TourModel;
}

function TourDateTimeSection() {
  const { t } = useTranslation();
  const { setValue, control, clearErrors } = useFormContext<TourListFormType>();

  const [startedAt, endedAt] = useWatch({
    control,
    name: ['startedAt', 'endedAt'],
  });

  React.useEffect(() => {
    try {
      if (
        startedAt &&
        endedAt &&
        differenceInCalendarDays(endedAt, startedAt) >= 0
      ) {
        setValue('day', differenceInCalendarDays(endedAt, startedAt) + 1, {
          shouldTouch: true,
          shouldValidate: true,
        });
        clearErrors('startedAt');
      } else {
        clearErrors('startedAt');
        setValue('day', 0, {
          shouldTouch: true,
          shouldValidate: true,
        });
      }
    } catch (e) {
      console.error(e);
    }
  }, [startedAt, endedAt, setValue, clearErrors]);

  return (
    <ModuleSectionContainer>
      <OneThirdContainer>
        <Input
          type="date-time"
          name="startedAt"
          label={t('common:from')}
          placeholder={t('common:from')}
          required
          style={{ flex: 1 }}
        />
      </OneThirdContainer>
      <OneThirdContainer>
        <Input
          type="date-time"
          name="endedAt"
          label={t('common:to')}
          placeholder={t('common:to')}
          required
          style={{ flex: 1 }}
        />
      </OneThirdContainer>
      <OneThirdContainer>
        <Input
          type="number"
          name="day"
          required
          label={t('common:days')}
          disabled
        />
      </OneThirdContainer>
    </ModuleSectionContainer>
  );
}

export default function TourTab(props: Props) {
  const { tour } = props;
  const { t } = useTranslation();
  const { setValue } = useFormContext<TourListFormType>();
  const { editable } = useFormState();

  return (
    <div className={classNames(styles.padtb16, styles.fullFlex)}>
      <SectionContainer>
        {!!tour && (
          <HalfContainer>
            <Input
              type="text"
              name="code"
              label={t('common:code')}
              placeholder={t('common:code')}
              required
            />
          </HalfContainer>
        )}
        <HalfContainer
          $style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
          }}
        >
          <TransactionAt
            name="transactionAt"
            clearable={false}
            label={t('common:transaction_at')}
            placeholder={t('common:transaction_at')}
            required
          />
        </HalfContainer>
      </SectionContainer>
      <ThirdContainer>
        <OneThirdContainer>
          <Input
            type="text"
            name="name"
            label={t('common:tour_name')}
            placeholder={t('common:tour_name')}
            required
          />
        </OneThirdContainer>
        <OneThirdContainer>
          <EnumSelectInput
            name="type"
            label={t('common:tour_type')}
            placeholder={t('common:tour_type')}
            enumType={EnumList.TourType}
            required
          />
        </OneThirdContainer>
        <OneThirdContainer>
          <TourCategorySelectInput
            name="tourCategoryId"
            required
            onAfterDetailChange={(value) => {
              if (editable) {
                setValue('adminCommission', value.adminCommission);
                setValue('operatorCommission', value.operatorCommission);
                setValue('salesCommission', value.salesCommission);
              }
            }}
          />
        </OneThirdContainer>
      </ThirdContainer>
      <ThirdContainer>
        <OneThirdContainer>
          <EmployeeSelectInput
            name="employeeId"
            required
            label={t('common:tour_admin')}
          />
        </OneThirdContainer>
        <OneThirdContainer>
          <EmployeeSelectInput
            name="operatorId"
            required
            label={t('common:tour_operator')}
          />
        </OneThirdContainer>
        <OneThirdContainer>
          <EmployeeSelectInput
            name="salesmanId"
            required
            label={t('common:tour_sales')}
          />
        </OneThirdContainer>
      </ThirdContainer>
      <ThirdContainer>
        <OneThirdContainer>
          <Input
            type="number"
            name="adminCommission"
            label={t('common:admin_commission')}
            placeholder={t('common:admin_commission')}
            required
          />
        </OneThirdContainer>
        <OneThirdContainer>
          <Input
            type="number"
            name="operatorCommission"
            label={t('common:operator_commission')}
            placeholder={t('common:operator_commission')}
            required
          />
        </OneThirdContainer>
        <OneThirdContainer>
          <Input
            type="number"
            name="salesCommission"
            label={t('common:sales_commission')}
            placeholder={t('common:sales_commission')}
            required
          />
        </OneThirdContainer>
      </ThirdContainer>
      <SectionContainer>
        <FullContainer>
          <Input
            type="text-area"
            name="description"
            label={t('common:description')}
            placeholder={t('common:description')}
          />
        </FullContainer>
      </SectionContainer>
      <TourDateTimeSection />
      <Separator gap={24} />
      <TourPriceSection />
      <Separator gap={40} />
      <OtherPriceSection />
      <Separator gap={40} />
    </div>
  );
}
const ThirdContainer = themedStyled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  [`@media screen and (max-width: calc(${stylesBreakpoint.lg} - 1px))`]: {
    width: '100%',
    flexDirection: 'column',
  },
});
